import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UploadSimple, CheckCircle, FileText } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";

export function ApplyDialog({ open, onOpenChange, job }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "" });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setForm({ name: "", email: "", phone: "", cover_letter: "" });
    setCv(null);
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cv) return toast.error("Please attach your CV");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("job_id", job.id);
    fd.append("name", form.name);
    fd.append("email", form.email);
    fd.append("phone", form.phone);
    fd.append("cover_letter", form.cover_letter);
    fd.append("cv", cv);
    try {
      await axios.post(`${API}/applications`, fd, { withCredentials: true });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="apply-dialog">
        {done ? (
          <div className="py-8 text-center" data-testid="apply-success">
            <CheckCircle size={56} weight="fill" className="mx-auto text-accent" />
            <h3 className="mt-4 font-heading text-2xl font-bold">Application submitted</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Thanks {form.name?.split(" ")[0] || ""}! Our team will review your details and be in touch if it's a match.
            </p>
            <Button className="mt-6 rounded-full" onClick={() => handleClose(false)} data-testid="apply-done-btn">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Apply for {job.title}</DialogTitle>
              <DialogDescription>Submit your application directly — no external sites.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="apply-form">
              <div>
                <Label htmlFor="ap-name">Full name *</Label>
                <Input id="ap-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="apply-name" />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="ap-email">Email *</Label>
                  <Input id="ap-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="apply-email" />
                </div>
                <div>
                  <Label htmlFor="ap-phone">Phone</Label>
                  <Input id="ap-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="apply-phone" />
                </div>
              </div>

              <div>
                <Label>CV / Résumé *</Label>
                <label
                  className="mt-1.5 flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed border-border bg-secondary/40 px-4 py-4 transition-colors hover:border-primary"
                  data-testid="apply-cv-dropzone"
                >
                  {cv ? <FileText size={22} className="text-accent" /> : <UploadSimple size={22} className="text-muted-foreground" />}
                  <span className="truncate text-sm">
                    {cv ? cv.name : "Upload PDF, DOC, DOCX (max 10MB)"}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.rtf,.odt,.txt"
                    className="hidden"
                    onChange={(e) => setCv(e.target.files?.[0] || null)}
                    data-testid="apply-cv-input"
                  />
                </label>
              </div>

              <div>
                <Label htmlFor="ap-cover">Cover letter / message</Label>
                <Textarea id="ap-cover" value={form.cover_letter} onChange={(e) => set("cover_letter", e.target.value)} className="mt-1.5 min-h-24" placeholder="Tell us why you're a great fit (optional)" data-testid="apply-cover" />
              </div>

              <Button type="submit" className="w-full rounded-full" disabled={submitting} data-testid="apply-submit-btn">
                {submitting ? "Submitting…" : "Submit application"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
